import { useCallback, useRef } from 'react';
import useWebSocketHook, { ReadyState } from 'react-use-websocket';
import { Subscription, UseWebSocket, UseWebSocketOptions, WebSocketStatus } from './websocket.types';

export const subscriptionsList = ['missionSubscription', 'chargingSubscription'] as const;

export const useWebSocketConfiguration = (options?: UseWebSocketOptions): UseWebSocket => {
    const { onOpen, onClose, onError, shouldReconnect = () => true, messageHandlers = {} } = options || {};

    const currentSubscriptions = useRef<Subscription[]>([]);

    const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocketHook(
        import.meta.env.VITE_API_WEBSOCKET_SERVICE,
        {
            onOpen: () => {
                onOpen?.() ?? console.log('[WS] Connected');
                resubscribe();
            },
            onClose: () => {
                onClose?.() ?? console.log('[WS] Disconnected');
            },
            onError: (event) => {
                onError?.(event) ?? console.error('[WS] Error:', event);
            },
            onMessage: (event) => {
                console.log('[WS] Message received:', event.data);

                try {
                    const { type, payload } = JSON.parse(event.data);
                    const handler = messageHandlers[type];
                    if (handler) {
                        handler(payload);
                    } else {
                        console.warn('[WS] No handler for message: type, payload', type, payload);
                    }
                } catch (err) {
                    console.error('[WS] Failed to parse message:', event.data, err);
                }
            },
            shouldReconnect,
            reconnectInterval: 3000,
        }
    );

    const send = useCallback(
        (action: string, payload: any) => {
            const message = JSON.stringify({ action, ...payload });
            console.log('[WS] Sending:', message);
            sendMessage(message);
        },
        [sendMessage]
    );

    const close = useCallback(() => {
        const socket = getWebSocket();
        if (socket && socket.readyState === WebSocket.OPEN) {
            console.log('[WS] Closing connection');
            socket.close();
        }
    }, [getWebSocket]);

    const sendSubscriptions = useCallback(
        (subscriptions: Subscription[]) => {
            const payload = subscriptions.reduce(
                (acc, subscription) => {
                    acc[subscription] = 'true';
                    return acc;
                },
                {} as Record<Subscription, 'true'>
            );

            console.log('[WS] Subscribing to subscriptions', ...subscriptions);
            send('subscription', payload);
        },
        [send]
    );

    const subscribe = useCallback(
        (...subscriptions: Subscription[]) => {
            const newSubscriptions = subscriptions.filter(
                (subscription) => !currentSubscriptions.current.includes(subscription)
            );

            if (newSubscriptions.length === 0) {
                console.log('[WS] Already subscribed to all subscriptions');
                return;
            }

            sendSubscriptions(newSubscriptions);
            currentSubscriptions.current.push(...newSubscriptions);
        },
        [sendSubscriptions]
    );

    const resubscribe = useCallback(() => {
        if (currentSubscriptions.current.length > 0) {
            sendSubscriptions(currentSubscriptions.current);
        }
    }, [sendSubscriptions]);

    const status = ReadyState[readyState] as WebSocketStatus;

    return {
        send,
        close,
        status,
        socket: getWebSocket(),
        lastMessage,
        subscribe,
        readyState,
    };
};
